import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

// --- Storage key ---

const SESSION_STORAGE_KEY = 'hetivo:master-scope';

// --- Types ---

export interface MasterContextValue {
  clearScope: () => void;
  selectSite: (siteId: string, territoryId: string) => void;
  selectTerritory: (territoryId: string) => void;
  siteId: string | null;
  territoryId: string | null;
}

interface PersistedScope {
  siteId: string | null;
  territoryId: string | null;
}

interface MasterContextProviderProps {
  children: ReactNode;
}

// --- Helpers ---

function isValidPersistedScope(value: unknown): value is PersistedScope {
  if (typeof value !== 'object' || value === null) return false;
  const obj = value as Record<string, unknown>;
  const isSiteIdValid = obj['siteId'] === null || typeof obj['siteId'] === 'string';
  const isTerritoryIdValid = obj['territoryId'] === null || typeof obj['territoryId'] === 'string';
  return isSiteIdValid && isTerritoryIdValid;
}

function readPersistedScope(): PersistedScope {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return buildEmptyScope();
    const parsed: unknown = JSON.parse(raw);
    if (!isValidPersistedScope(parsed)) return buildEmptyScope();
    return parsed;
  } catch {
    return buildEmptyScope();
  }
}

function buildEmptyScope(): PersistedScope {
  return { siteId: null, territoryId: null };
}

// --- Context ---

const MasterContext = createContext<MasterContextValue | null>(null);

// --- Provider ---

export function MasterContextProvider({ children }: MasterContextProviderProps): React.JSX.Element {
  const [scope, setScope] = useState<PersistedScope>(readPersistedScope);

  useEffect(() => {
    try {
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(scope));
    } catch (error: unknown) {
      console.warn('[MasterContext] Failed to persist scope to sessionStorage:', error);
    }
  }, [scope]);

  const selectTerritory = useCallback((territoryId: string): void => {
    setScope({ siteId: null, territoryId });
  }, []);

  const selectSite = useCallback((siteId: string, territoryId: string): void => {
    setScope({ siteId, territoryId });
  }, []);

  const clearScope = useCallback((): void => {
    setScope(buildEmptyScope());
  }, []);

  const value = useMemo<MasterContextValue>(
    () => ({
      clearScope,
      selectSite,
      selectTerritory,
      siteId: scope.siteId,
      territoryId: scope.territoryId,
    }),
    [clearScope, selectSite, selectTerritory, scope.siteId, scope.territoryId],
  );

  return <MasterContext.Provider value={value}>{children}</MasterContext.Provider>;
}

// --- Hook ---

export function useMasterContext(): MasterContextValue {
  const context = useContext(MasterContext);
  if (!context) {
    throw new Error('useMasterContext must be used within a MasterContextProvider');
  }
  return context;
}
